import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { RevealLine } from "@/components/RevealLine";

const TIERS = [
  {
    id: "pro",
    name: "Pro",
    tagline: "For the individual chippy.",
    monthly: { price: "$10", note: "per month (incl. GST)" },
    yearly: { price: "$95", original: "$120", note: "per year (incl. GST) — save 21%" },
    features: [
      "All Formulas calculators",
      "All access to App Features",
      "Unlimited NCC Bookmarks",
      "Unlimited Draft A Plan saves",
      "Daily Building Logs",
    ],
  },
  {
    id: "premium-team",
    name: "Premium Team",
    tagline: "Up to 5 users — boss + crew.",
    badge: "Best value for crews",
    highlight: true,
    monthly: { price: "$40", note: "per month (incl. GST) · 5 users" },
    yearly: {
      price: "$370",
      original: "$480",
      note: "per year (incl. GST) · 5 users — save 23%",
    },
    features: [
      "Everything in Pro",
      "Up to 5 team members",
      "Shared Bookmarks & Drafts",
      "One bill, simple admin",
      "Shared Daily Building Logs",
    ],
  },
  {
    id: "ultimate-team",
    name: "Ultimate Team",
    tagline: "Bigger crew? Tailored seats & price for your business.",
    badge: "By quote",
    quote: true,
    features: [
      "Custom number of seats (build to your crew)",
      "Everything in the Premium Team plan",
      "Shared Daily Building Logs across the crew",
      "Job/Delivery/Trade hours rolled up for the boss",
      "Direct support from the ChippyMate team",
    ],
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);

  const handleSubscribe = (tierName) => {
    toast.info(`${tierName} subscriptions are handled inside the ChippyMate app.`);
  };

  return (
    <motion.main
      data-testid="pricing-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-6xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Pricing</RevealLine>
      </h1>
      <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
        Simple pricing in AUD, GST included. Cancel anytime — your data stays on
        your account.
      </p>

      <div
        data-testid="billing-toggle"
        className="mt-10 inline-flex items-center rounded-full border border-white/15 bg-black/30 p-1.5 backdrop-blur-xl"
      >
        <button
          data-testid="billing-toggle-monthly"
          onClick={() => setYearly(false)}
          className={`rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${
            !yearly ? "bg-[#3969E7] text-white" : "text-white/60 hover:text-white"
          }`}
        >
          Monthly
        </button>
        <button
          data-testid="billing-toggle-yearly"
          onClick={() => setYearly(true)}
          className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-bold transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50 ${
            yearly ? "bg-[#3969E7] text-white" : "text-white/60 hover:text-white"
          }`}
        >
          Yearly
          <span className="rounded-full bg-[#07B9CE]/20 px-2.5 py-0.5 text-xs font-bold text-[#5ee5f2]">
            Save up to 21%
          </span>
        </button>
      </div>

      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {TIERS.map((tier, i) => (
          <motion.div
            key={tier.id}
            data-testid={`pricing-card-${tier.id}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{
              duration: 0.6,
              delay: i * 0.1,
              ease: [0.76, 0, 0.24, 1],
            }}
            className={`relative flex flex-col rounded-2xl border p-7 backdrop-blur-xl sm:p-8 ${
              tier.highlight
                ? "border-[#07B9CE]/50 bg-black/40 shadow-[0_0_50px_rgba(7,185,206,0.12)]"
                : "border-white/10 bg-black/30"
            }`}
          >
            {tier.badge && (
              <span className="absolute -top-3.5 right-6 rounded-full bg-[#07B9CE] px-3.5 py-1 text-xs font-bold text-[#06283c]">
                {tier.badge}
              </span>
            )}
            <h2 className="font-brand text-4xl tracking-wide text-white">
              {tier.name}
            </h2>
            <p className="mt-2 text-sm text-white/60">{tier.tagline}</p>

            {tier.quote ? (
              <div className="mt-7">
                <p className="text-4xl font-extrabold text-white">
                  Contact{" "}
                  <span className="text-lg font-semibold text-white/60">
                    for quote
                  </span>
                </p>
                <p className="mt-2 text-sm text-white/50">
                  Custom seat count · monthly or annual
                </p>
              </div>
            ) : (
              <div className="mt-7">
                <p className="flex items-baseline gap-3">
                  <span
                    data-testid={`price-${tier.id}`}
                    className="text-5xl font-extrabold tracking-tight text-white"
                  >
                    {yearly ? tier.yearly.price : tier.monthly.price}
                  </span>
                  {yearly && tier.yearly.original && (
                    <span
                      data-testid={`original-price-${tier.id}`}
                      className="text-2xl font-semibold text-white/40 line-through"
                    >
                      {tier.yearly.original}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-white/50">
                    AUD
                  </span>
                </p>
                <p className="mt-2 text-sm text-white/50">
                  {yearly ? tier.yearly.note : tier.monthly.note}
                </p>
              </div>
            )}

            <ul className="mt-8 flex-1 space-y-3.5">
              {tier.features.map((feature) => (
                <li
                  key={feature}
                  className="flex items-start gap-3 text-sm text-white/80"
                >
                  <Check
                    className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400"
                    strokeWidth={3}
                  />
                  {feature}
                </li>
              ))}
            </ul>

            {tier.quote ? (
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}>
                <Link
                  data-testid="quote-button"
                  to="/contact"
                  className="mt-8 flex items-center justify-center gap-2 rounded-full bg-[#07B9CE] px-6 py-3.5 text-sm font-bold text-[#06283c] transition-opacity duration-300 hover:opacity-90"
                >
                  <Mail className="h-4 w-4" />
                  Get a quote
                </Link>
              </motion.div>
            ) : (
              <motion.button
                data-testid={`subscribe-button-${tier.id}`}
                onClick={() => handleSubscribe(tier.name)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={`mt-8 rounded-full px-6 py-3.5 text-sm font-bold transition-opacity duration-300 hover:opacity-90 ${
                  tier.highlight
                    ? "bg-[#07B9CE] text-[#06283c]"
                    : "bg-[#3969E7] text-white"
                }`}
              >
                Subscribe now
              </motion.button>
            )}
          </motion.div>
        ))}
      </div>

      <p className="mt-12 text-center text-xs leading-relaxed text-white/40 sm:text-sm">
        Subscriptions are purchased and managed inside the Offcut app. By
        subscribing you agree to ChippyMate - Offcut's Terms &amp; Conditions.
        Cancel anytime — your data stays on your account.
      </p>
    </motion.main>
  );
}
